/**
 * Roster, roles, permissions, teams and invitations for the active workspace.
 *
 * Every control here is gated on the same permission the API checks, read from
 * `TenantContext` — so a member sees the teams they are in and nothing else,
 * while an admin sees the whole tab. The gating is a courtesy: each action is
 * refused again on the server, and this only decides what is worth showing.
 */

import { useState } from 'react';
import { Loader2, Plus, ShieldCheck, Trash2, UserMinus, UserPlus } from 'lucide-react';

import { MAX_TENANT_ADMINS } from '@app/common';
import type { PermissionOverride, TenantMemberSummary, TenantRole } from '@app/common';
import {
  changeMemberRole,
  createTeam,
  deleteTeam,
  inviteMember,
  removeMember,
  revokeInvitation,
  setMemberPermissions,
  setTeamMembers,
} from '../../api/tenants.api';
import { Badge } from '../../components/core/Badge';
import { Button } from '../../components/core/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/core/Card';
import { PermissionEditor } from '../../components/dashboard/team/PermissionEditor';
import { useTenant } from '../../context/TenantContext';
import { useTranslation } from '../../context/LanguageContext';
import { useTeamData } from '../../hooks/useTeamData';

export function TeamPage() {
  const { t } = useTranslation();
  const { activeTenantId, can, refresh: refreshTenants } = useTenant();
  const { members, teams, invitations, loading, error, refresh } = useTeamData();

  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingPermissionsFor, setEditingPermissionsFor] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TenantRole>('member');
  const [teamName, setTeamName] = useState('');

  const canManageMembers = can('member.invite');
  const canRemove = can('member.remove');
  const canManageRoles = can('member.role.manage');
  const canManagePermissions = can('member.permission.manage');
  const canManageTeams = can('team.manage');

  const adminCount = members.filter((member) => member.role === 'admin').length;

  /**
   * One wrapper for every mutation, so a failure always surfaces the backend's
   * own message rather than a generic one. Those messages are written to be
   * shown — "A workspace can have at most 3 admins" is the whole explanation.
   */
  const run = async (key: string, action: () => Promise<unknown>, reloadTenants = false) => {
    setBusy(key);
    setActionError(null);
    try {
      await action();
      refresh();
      if (reloadTenants) refreshTenants();
    } catch (err: unknown) {
      setActionError((err as { message?: string }).message ?? t('team.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  if (!activeTenantId) {
    return (
      <p className="py-20 text-center text-sm text-slate-500">{t('team.noWorkspace')}</p>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return <p className="py-20 text-center text-sm text-rose-600">{error}</p>;
  }

  return (
    <div className="space-y-5">
      {actionError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionError}
        </div>
      )}

      {canManageMembers && (
        <Card>
          <CardHeader>
            <CardTitle>{t('team.invite.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!inviteEmail.trim()) return;
                void run('invite', async () => {
                  await inviteMember(activeTenantId, {
                    email: inviteEmail.trim(),
                    role: inviteRole,
                  });
                  setInviteEmail('');
                });
              }}
            >
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('team.invite.emailPlaceholder')}
                className="h-10 min-w-56 flex-1 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-brand-400"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as TenantRole)}
                className="h-10 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-brand-400"
              >
                <option value="member">{t('workspace.roleMember')}</option>
                <option value="admin" disabled={adminCount >= MAX_TENANT_ADMINS}>
                  {t('workspace.roleAdmin')}
                </option>
              </select>
              <Button type="submit" size="sm" disabled={busy === 'invite'}>
                {busy === 'invite' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5" />
                )}
                {t('team.invite.submit')}
              </Button>
            </form>
            <p className="mt-2 text-xs text-slate-400">
              {t('team.invite.adminCount', { count: adminCount, max: MAX_TENANT_ADMINS })}
            </p>
          </CardContent>
        </Card>
      )}

      {canManageMembers && members.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('team.members.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                teams={teams}
                busy={busy}
                canRemove={canRemove}
                canManageRoles={canManageRoles}
                canManagePermissions={canManagePermissions}
                adminCount={adminCount}
                editing={editingPermissionsFor === member.userId}
                onToggleEditing={() =>
                  setEditingPermissionsFor((current) =>
                    current === member.userId ? null : member.userId
                  )
                }
                onChangeRole={(role) =>
                  void run(
                    `role-${member.userId}`,
                    () => changeMemberRole(activeTenantId, member.userId, role),
                    // A promotion changes the caller's own permissions when they
                    // promote themselves, so the tenant context has to reload too.
                    true
                  )
                }
                onRemove={() =>
                  void run(`remove-${member.userId}`, () =>
                    removeMember(activeTenantId, member.userId)
                  )
                }
                onSavePermissions={async (overrides: PermissionOverride[]) => {
                  await setMemberPermissions(activeTenantId, member.userId, overrides);
                  setEditingPermissionsFor(null);
                  refresh();
                  refreshTenants();
                }}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {canManageMembers && invitations.some((i) => i.status === 'pending') && (
        <Card>
          <CardHeader>
            <CardTitle>{t('team.invitations.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {invitations
              .filter((invitation) => invitation.status === 'pending')
              .map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-slate-700">
                      {invitation.email}
                    </span>
                    <span className="block text-xs text-slate-400">
                      {t('team.invitations.expires', {
                        date: new Date(invitation.expiresAt).toLocaleDateString(),
                      })}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Badge variant="secondary">
                      {t(
                        invitation.role === 'admin'
                          ? 'workspace.roleAdmin'
                          : 'workspace.roleMember'
                      )}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy === `revoke-${invitation.id}`}
                      onClick={() =>
                        void run(`revoke-${invitation.id}`, () =>
                          revokeInvitation(activeTenantId, invitation.id)
                        )
                      }
                    >
                      {t('team.invitations.revoke')}
                    </Button>
                  </span>
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('team.teams.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {canManageTeams && (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (!teamName.trim()) return;
                void run('create-team', async () => {
                  await createTeam(activeTenantId, teamName.trim());
                  setTeamName('');
                });
              }}
            >
              <input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder={t('team.teams.namePlaceholder')}
                className="h-10 flex-1 rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-brand-400"
              />
              <Button type="submit" size="sm" disabled={busy === 'create-team'}>
                <Plus className="h-3.5 w-3.5" />
                {t('team.teams.create')}
              </Button>
            </form>
          )}

          {teams.length === 0 && (
            <p className="py-4 text-center text-xs text-slate-400">{t('team.teams.empty')}</p>
          )}

          {teams.map((team) => (
            <div key={team.id} className="rounded-xl border border-slate-200 px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-800">
                    {team.name}
                  </span>
                  <span className="block text-xs text-slate-400">
                    {t('workspace.members', { count: team.memberIds.length })}
                  </span>
                </span>
                {canManageTeams && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('team.teams.delete')}
                    disabled={busy === `delete-team-${team.id}`}
                    onClick={() =>
                      void run(`delete-team-${team.id}`, () => deleteTeam(activeTenantId, team.id))
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                  </Button>
                )}
              </div>

              {canManageTeams && members.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 border-t border-slate-100 pt-2">
                  {members.map((member) => {
                    const isIn = team.memberIds.includes(member.userId);
                    return (
                      <button
                        key={member.userId}
                        type="button"
                        aria-pressed={isIn}
                        disabled={busy === `team-members-${team.id}`}
                        onClick={() =>
                          void run(`team-members-${team.id}`, () =>
                            setTeamMembers(
                              activeTenantId,
                              team.id,
                              isIn
                                ? team.memberIds.filter((id) => id !== member.userId)
                                : [...team.memberIds, member.userId]
                            )
                          )
                        }
                        className={
                          isIn
                            ? 'rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700'
                            : 'rounded-full border border-slate-200 px-2.5 py-0.5 text-xs text-slate-500 hover:bg-slate-50'
                        }
                      >
                        {member.displayName ?? member.email ?? member.userId.slice(0, 8)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function MemberRow({
  member,
  teams,
  busy,
  canRemove,
  canManageRoles,
  canManagePermissions,
  adminCount,
  editing,
  onToggleEditing,
  onChangeRole,
  onRemove,
  onSavePermissions,
}: Readonly<{
  member: TenantMemberSummary;
  teams: { id: string; name: string }[];
  busy: string | null;
  canRemove: boolean;
  canManageRoles: boolean;
  canManagePermissions: boolean;
  adminCount: number;
  editing: boolean;
  onToggleEditing: () => void;
  onChangeRole: (role: TenantRole) => void;
  onRemove: () => void;
  onSavePermissions: (overrides: PermissionOverride[]) => Promise<void>;
}>) {
  const { t } = useTranslation();
  const memberTeams = teams.filter((team) => member.teamIds.includes(team.id));

  // Demoting the only admin is refused by the database, so the control is
  // disabled rather than left to fail — the refusal is correct but arrives as an
  // error on an action that looked available.
  const isLastAdmin = member.role === 'admin' && adminCount <= 1;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-800">
            {member.displayName ?? member.email ?? member.userId.slice(0, 8)}
          </span>
          <span className="block truncate text-xs text-slate-400">
            {member.email}
            {memberTeams.length > 0 && ` · ${memberTeams.map((team) => team.name).join(', ')}`}
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-1.5">
          {member.overrides.length > 0 && (
            <Badge variant="warning">
              {t('team.members.overrides', { count: member.overrides.length })}
            </Badge>
          )}

          {canManageRoles ? (
            <select
              value={member.role}
              disabled={isLastAdmin || busy === `role-${member.userId}`}
              onChange={(e) => onChangeRole(e.target.value as TenantRole)}
              className="h-8 rounded-lg border border-slate-200 px-2 text-xs outline-none focus:border-brand-400 disabled:opacity-50"
            >
              <option value="member">{t('workspace.roleMember')}</option>
              <option value="admin">{t('workspace.roleAdmin')}</option>
            </select>
          ) : (
            <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
              {t(member.role === 'admin' ? 'workspace.roleAdmin' : 'workspace.roleMember')}
            </Badge>
          )}

          {canManagePermissions && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('team.permissions.edit')}
              onClick={onToggleEditing}
            >
              <ShieldCheck className="h-3.5 w-3.5 text-slate-500" />
            </Button>
          )}

          {canRemove && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('team.members.remove')}
              disabled={busy === `remove-${member.userId}`}
              onClick={onRemove}
            >
              <UserMinus className="h-3.5 w-3.5 text-rose-500" />
            </Button>
          )}
        </span>
      </div>

      {editing && (
        <PermissionEditor
          member={member}
          onSave={onSavePermissions}
          onCancel={onToggleEditing}
        />
      )}
    </div>
  );
}
